import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import authService from '../../services/authService';
import './Admin.css';

const STEP_AMOUNT = 1;
const STEP_PAYMENT = 2;
const STEP_REVIEW = 3;
const STEP_PROCESSING = 4;
const STEP_SUCCESS = 5;

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000, 2500, 5000];

const CreditPurchaseInner = ({ organizationId, teamId, onSuccess, onClose, stripePromise }) => {
  const stripe = useStripe();
  const elements = useElements();
  const cardElementRef = React.useRef(null);
  const [step, setStep] = useState(STEP_AMOUNT);
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  // Load estimate when amount changes
  useEffect(() => {
    if (step === STEP_AMOUNT && amount >= 10 && amount <= 10000) {
      loadEstimate();
    }
  }, [amount, step]);

  const isTeamMode = !!teamId && !organizationId;

  const loadEstimate = async () => {
    if (amount < 10 || amount > 10000) return;
    setLoadingEstimate(true);
    setError('');
    try {
      const t = authService.getToken();
      const url = !isTeamMode
        ? `/api/credits/purchase/estimate?orgId=${organizationId}&amount=${amount}`
        : `/api/credits/purchase/estimate?teamId=${teamId}&amount=${amount}`;
      const res = await fetch(url, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setEstimate(data);
      } else {
        const errorData = await res.json().catch(() => ({ message: 'Failed to load estimate' }));
        setError(errorData.message || 'Failed to load estimate');
      }
    } catch (err) {
      setError(err.message || 'Failed to load estimate');
    } finally {
      setLoadingEstimate(false);
    }
  };

  const handleAmountSelect = (selectedAmount) => {
    setAmount(selectedAmount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 10 && numValue <= 10000) {
      setAmount(numValue);
    }
  };

  const handleNext = async () => {
    if (step === STEP_AMOUNT) {
      if (amount < 10 || amount > 10000) {
        setError('Amount must be between $10 and $10,000');
        return;
      }
      setStep(STEP_REVIEW); // Show review BEFORE payment step
    } else if (step === STEP_REVIEW) {
      setStep(STEP_PAYMENT); // Move to payment step
    } else if (step === STEP_PAYMENT) {
      if (!stripe || !elements) {
        setError('Stripe not loaded');
        return;
      }
      // Small delay to ensure CardElement is fully mounted and ready
      await new Promise(resolve => setTimeout(resolve, 100));
      // Verify card element is available before proceeding - try ref first, then getElement
      let card = cardElementRef.current || elements.getElement(CardElement);
      if (!card) {
        setError('Please enter your card details. Make sure the card form is visible.');
        return;
      }
      // Confirm payment immediately when leaving payment step (like working code)
      await handlePurchase();
    }
  };

  const handlePurchase = async () => {
    if (!stripe || !elements) {
      setError('Stripe not loaded. Please refresh the page.');
      return;
    }
    
    // CRITICAL: Get card element BEFORE changing step (CardElement gets unmounted when step changes)
    let card = cardElementRef.current || elements.getElement(CardElement);
    if (!card) {
      setError('Card details are required. Please enter your card information.');
      return;
    }
    
    // Don't change step yet - keep CardElement mounted for Stripe
    setProcessing(true);
    setError('');

    try {
      const t = authService.getToken();
      
      // Create payment intent
      const intentUrl = !isTeamMode ? '/api/credits/purchase/intent' : '/api/credits/purchase/intent/team';
      const intentRes = await fetch(intentUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
          body: JSON.stringify(
            !isTeamMode
              ? { organizationId: organizationId, amount: amount }
              : { teamId: teamId, amount: amount }
          )
      });

      if (!intentRes.ok) {
        const errorData = await intentRes.json().catch(() => ({ message: 'Failed to create payment intent' }));
        throw new Error(errorData.message || 'Failed to create payment intent');
      }

      const intentData = await intentRes.json();
      setClientSecret(intentData.client_secret);
      setTransactionId(intentData.transaction_id);
      setPaymentIntentId(intentData.payment_intent_id);

      // Card element already retrieved above - use it while still mounted

      // Confirm payment
      const result = await stripe.confirmCardPayment(intentData.client_secret, {
        payment_method: {
          card: card,
          billing_details: {
            // Add billing details if needed
          }
        }
      });

      if (result.error) {
        throw new Error(result.error.message || 'Payment failed');
      }

      if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        // Confirm purchase on backend
        const confirmUrl = !isTeamMode ? '/api/credits/purchase/confirm' : '/api/credits/purchase/confirm/team';
        const confirmRes = await fetch(confirmUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(t ? { Authorization: `Bearer ${t}` } : {})
          },
          body: JSON.stringify({
            transactionId: intentData.transaction_id,
            paymentIntentId: intentData.payment_intent_id
          })
        });

        if (!confirmRes.ok) {
          const errorData = await confirmRes.json().catch(() => ({ message: 'Failed to confirm purchase' }));
          throw new Error(errorData.message || 'Failed to confirm purchase');
        }

        const confirmData = await confirmRes.json();
        setSuccessData(confirmData);
        // NOW safe to change step - payment is complete
        setStep(STEP_SUCCESS);
        
        // Trigger balance refresh
        try {
          localStorage.setItem('balanceRefreshTick', String(Date.now()));
          setTimeout(() => {
            try {
              localStorage.setItem('balanceRefreshTick', String(Date.now()));
            } catch {}
          }, 1500);
        } catch {}

        // Call success callback
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        throw new Error('Payment not completed');
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
      setStep(STEP_REVIEW);
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    if (step > STEP_AMOUNT) {
      setStep(step - 1);
      setError('');
    }
  };

  const handleClose = () => {
    if (step === STEP_PROCESSING) return; // Don't allow closing during processing
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div className="modal-header">
          <h2>Purchase Credits</h2>
          {step !== STEP_PROCESSING && (
            <button className="modal-close" onClick={handleClose}>×</button>
          )}
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {/* Step 1: Amount Selection */}
          {step === STEP_AMOUNT && (
            <div>
              <h3 style={{ marginBottom: '1rem' }}>Select Amount</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {PRESET_AMOUNTS.map((preset) => (
                  <button
                    key={preset}
                    className={`btn ${amount === preset ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handleAmountSelect(preset)}
                    style={{ padding: '0.75rem' }}
                  >
                    ${preset}
                  </button>
                ))}
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Or enter custom amount ($10 - $10,000)
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Enter amount"
                  min="10"
                  max="10000"
                  step="0.01"
                />
              </div>
              {loadingEstimate ? (
                <div style={{ padding: '1rem', textAlign: 'center' }}>Loading estimate...</div>
              ) : estimate ? (
                <div className="dashboard-card" style={{ padding: '1rem', marginTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Amount:</span>
                    <strong>${estimate.amount.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Processing Fee (3%):</span>
                    <span>${estimate.processing_fee.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #ddd' }}>
                    <span>Total:</span>
                    <strong>${estimate.total.toFixed(2)}</strong>
                  </div>
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Credits Awarded:</span>
                      <strong>${estimate.credits_awarded.toFixed(2)}</strong>
                    </div>
                    {estimate.bonus_multiplier > 1 && (
                      <div style={{ fontSize: '0.875rem', color: '#2e7d32' }}>
                        {((estimate.bonus_multiplier - 1) * 100).toFixed(0)}% bonus applied!
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Step 2: Payment Method - Keep CardElement mounted during payment AND processing (critical for Stripe) */}
          {(step === STEP_PAYMENT || processing) && (
            <div style={{ display: processing ? 'none' : 'block' }}>
              <h3 style={{ marginBottom: '1rem' }}>Payment Method</h3>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Card Details
                </label>
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}>
                  <CardElement
                    onReady={(element) => {
                      cardElementRef.current = element;
                    }}
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Keep CardElement mounted during review - hidden but accessible (positioned off-screen) */}
          {step === STEP_REVIEW && (
            <div style={{ position: 'absolute', left: '-9999px', width: '400px', height: '50px' }}>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            </div>
          )}

          {/* Step 3: Review */}
          {step === STEP_REVIEW && estimate && (
            <div>
              <h3 style={{ marginBottom: '1rem' }}>Review Purchase</h3>
              <div className="dashboard-card" style={{ padding: '1rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Purchase Summary</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Amount:</span>
                  <span>${estimate.amount.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Processing Fee:</span>
                  <span>${estimate.processing_fee.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #ddd' }}>
                  <span><strong>Total:</strong></span>
                  <span><strong>${estimate.total.toFixed(2)}</strong></span>
                </div>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span><strong>Credits Awarded:</strong></span>
                    <span><strong>${estimate.credits_awarded.toFixed(2)}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Processing */}
          {step === STEP_PROCESSING && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
              <h3>Processing Payment...</h3>
              <p style={{ color: '#666', marginTop: '0.5rem' }}>Please wait while we process your payment.</p>
            </div>
          )}

          {/* Step 5: Success */}
          {step === STEP_SUCCESS && successData && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h3 style={{ color: '#2e7d32' }}>Purchase Successful!</h3>
              <div className="dashboard-card" style={{ padding: '1rem', marginTop: '1rem', textAlign: 'left' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Credits Added:</strong> ${successData.credits_awarded?.toFixed(2) || estimate?.credits_awarded.toFixed(2) || '0.00'}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>New Balance:</strong> ${successData.new_balance?.toFixed(2) || '0.00'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  Transaction ID: {transactionId?.substring(0, 8)}...
                </div>
              </div>
              <p style={{ color: '#666', marginTop: '1rem' }}>Your credits have been added to your account.</p>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
          {step > STEP_AMOUNT && step < STEP_PROCESSING && (
            <button className="btn btn-secondary" onClick={handleBack} disabled={processing}>
              Back
            </button>
          )}
          <div style={{ marginLeft: 'auto' }}>
            {step < STEP_SUCCESS && step !== STEP_PROCESSING && (
              <button
                className="btn btn-primary"
                onClick={handleNext}
                disabled={processing || (step === STEP_AMOUNT && (!estimate || loadingEstimate))}
              >
                {step === STEP_REVIEW ? 'Confirm Purchase' : 'Next'}
              </button>
            )}
            {step === STEP_SUCCESS && (
              <button className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CreditPurchaseModal = ({ organizationId, teamId, onSuccess, onClose }) => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/payments/config');
        const data = await res.json();
        if (data.publishableKey) {
          setStripePromise(loadStripe(data.publishableKey));
        }
      } catch (err) {
        console.error('Failed to load Stripe config:', err);
      }
    })();
  }, []);

  if (!stripePromise) {
    return (
      <div className="modal-overlay">
        <div className="modal-content admin-modal">
          <div className="modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
            Loading payment system...
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CreditPurchaseInner
        organizationId={organizationId}
        teamId={teamId}
        onSuccess={onSuccess}
        onClose={onClose}
        stripePromise={stripePromise}
      />
    </Elements>
  );
};

export default CreditPurchaseModal;

